/**
 * Call recording on this side, the pure parts (docs/call-recording.md):
 * which tracks this device records, at what size and bitrate, where each
 * chunk goes, and how its clock is placed on the server's. Every call is
 * recorded unless someone in it turns recording off. Pure, so tested.
 */

/** What one recorder captures. Matches the compile job's SegmentKind. */
export type SegmentKind = 'cam' | 'mix' | 'stage' | 'share';

/** Seconds of media per uploaded chunk. */
export const TIMESLICE_MS = 4000;
/** How often an open recording says this device is still there. */
export const HEARTBEAT_MS = 30_000;

/** The first webm type this browser records, video or audio only. Null where it records none. */
export function pickMimeType(isSupported: (t: string) => boolean, video: boolean): string | null {
  const wanted = video
    ? ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm']
    : ['audio/webm;codecs=opus', 'audio/webm'];
  return wanted.find((t) => isSupported(t)) ?? null;
}

/** Bits per second for a kind: the camera near what the call sends, the stage higher. */
export function bitrates(kind: SegmentKind, phone: boolean): { video: number; audio: number } {
  switch (kind) {
    case 'cam': return { video: 1_000_000, audio: 64_000 };
    case 'stage': return { video: phone ? 1_500_000 : 2_500_000, audio: 64_000 };
    case 'share': return { video: 2_000_000, audio: 64_000 };
    case 'mix': return { video: 0, audio: 96_000 };
  }
}

/** The stage's canvas: 720p at 30 on a computer, 540p at 24 on a phone. */
export function stageSize(phone: boolean): { w: number; h: number; fps: number } {
  return phone ? { w: 960, h: 540, fps: 24 } : { w: 1280, h: 720, fps: 30 };
}

export interface ClockSample {
  /** Local clock when the request went out. */
  sentMs: number;
  /** Local clock when the answer came back. */
  receivedMs: number;
  /** The server's clock in the answer. */
  serverMs: number;
}

/**
 * Server time minus local time, from the sample with the shortest round
 * trip, taking the server's reading as made halfway through it.
 */
export function clockOffset(samples: ClockSample[]): { offsetMs: number; rttMs: number } | null {
  let best: ClockSample | null = null;
  for (const s of samples) {
    if (s.receivedMs < s.sentMs || !Number.isFinite(s.serverMs)) continue;
    if (!best || s.receivedMs - s.sentMs < best.receivedMs - best.sentMs) best = s;
  }
  if (!best) return null;
  const rttMs = best.receivedMs - best.sentMs;
  return { offsetMs: Math.round(best.serverMs - (best.sentMs + rttMs / 2)), rttMs };
}

/** call-recordings/{rid}/{uid}/{segmentId}/, as storage.rules has it. */
export function segmentFolder(recordingId: string, uid: string, segmentId: string): string {
  return `call-recordings/${recordingId}/${uid}/${segmentId}/`;
}

/** A chunk's path: six digits, from 000000. */
export function chunkPath(recordingId: string, uid: string, segmentId: string, seq: number): string {
  return `${segmentFolder(recordingId, uid, segmentId)}${String(seq).padStart(6, '0')}.webm`;
}

export function manifestPath(recordingId: string, uid: string, segmentId: string): string {
  return `${segmentFolder(recordingId, uid, segmentId)}manifest.json`;
}

/** What the compile job reads to place a segment. */
export interface SegmentManifest {
  uid: string;
  segmentId: string;
  kind: SegmentKind;
  startedAtServerMs: number;
  endedAtServerMs: number | null;
  rttMs: number | null;
  mimeType: string;
  chunkCount: number;
}

/** A segment id: the kind, the start and a random tail, safe in a path. */
export function segmentId(kind: SegmentKind, startedAtServerMs: number, rand: string): string {
  return `${kind}-${Math.round(startedAtServerMs)}-${rand.replace(/[^a-z0-9]/gi, '').slice(0, 8)}`;
}

/**
 * Which recorders this side runs. Everyone records their own camera and
 * mic. The coach and the player also record the call's sound as they hear
 * it, so the file keeps its sound if one side's upload is lost. The player
 * records the match as their screen shows it, the lesson's main picture.
 * Anyone sharing a screen records the share.
 */
export function segmentsWanted(p: {
  role: 'coach' | 'player' | 'guest'; hasCamOrMic: boolean; matchOpen: boolean; sharing: boolean;
}): SegmentKind[] {
  const out: SegmentKind[] = [];
  if (p.hasCamOrMic) out.push('cam');
  if (p.role !== 'guest') out.push('mix');
  if (p.role === 'player' && p.matchOpen) out.push('stage');
  if (p.sharing) out.push('share');
  return out;
}

/** Where a frame of w x h sits inside a box, whole and centred (object-contain). */
export function containRect(w: number, h: number, box: { x: number; y: number; w: number; h: number }):
  { x: number; y: number; w: number; h: number } | null {
  if (!w || !h || !box.w || !box.h) return null;
  const s = Math.min(box.w / w, box.h / h);
  const dw = w * s;
  const dh = h * s;
  return { x: box.x + (box.w - dw) / 2, y: box.y + (box.h - dh) / 2, w: dw, h: dh };
}

/**
 * Whether a video from this url can be drawn to a canvas and still be
 * recorded: our storage buckets answer with CORS headers (cors.json), as
 * does our own origin. Anything else would taint the canvas.
 */
export function corsReadable(url: string, origin: string): boolean {
  if (url.startsWith('blob:') || url.startsWith('data:')) return true;
  try {
    const u = new URL(url, origin);
    if (u.origin === origin) return true;
    return u.hostname === 'firebasestorage.googleapis.com' || u.hostname === 'storage.googleapis.com'
      || u.hostname.endsWith('.firebasestorage.app');
  } catch {
    return false;
  }
}

/** The bar button's name, which is also its tooltip. */
export function recordButtonLabel(enabled: boolean): string {
  return enabled ? 'Stop Recording' : 'Start Recording';
}

/** Who may turn recording off or on: anyone, except a guest in a lesson. */
export function canToggleRecording(p: { lesson: boolean; role: 'coach' | 'player' | 'guest' | null }): boolean {
  if (!p.role) return false;
  return !(p.lesson && p.role === 'guest');
}
